import React, {useMemo, useState} from "react";
import {AlertTriangle, Ban, Bell, CheckCircle, ChevronLeft, ChevronRight, Filter} from "lucide-react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {
    formatDateBR,
    formatMoney,
    getInadimplenciaSeverity,
    getLicencaStatusPalette
} from "./financeiroUtils";
import ActionConfirmationDialog from "./components/ActionConfirmationDialog";

const tableHeaderCellSx = {
    px: 3,
    py: 1.75,
    color: '#374151',
    fontSize: 14,
    fontWeight: 500,
    borderBottom: '1px solid #e5e7eb',
};

const tableBodyCellSx = {
    px: 3,
    py: 2,
    borderBottom: '1px solid #e5e7eb',
};

export default function FinanceiroInadimplenciaPage({
    inadimplentes,
    inadimplenciaLoading,
    onBlockLicense,
    onReactivateLicense,
    onNotifyCollection,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [actionDialog, setActionDialog] = useState({open: false, type: null, payload: null});
    const [inadimplenciaPage, setInadimplenciaPage] = useState(1);
    const itemsPerPage = 10;

    const filteredInadimplentes = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return inadimplentes.filter((item) => {
            const companyName = (item.empresa_nome || item.empresa?.nome || '').toLowerCase();
            return !query || companyName.includes(query);
        });
    }, [inadimplentes, searchQuery]);

    const inadimplenciaTotalPages = Math.max(1, Math.ceil(filteredInadimplentes.length / itemsPerPage));
    const safeInadimplenciaPage = Math.min(inadimplenciaPage, inadimplenciaTotalPages);
    const inadimplenciaStartIndex = (safeInadimplenciaPage - 1) * itemsPerPage;
    const paginatedInadimplentes = filteredInadimplentes.slice(inadimplenciaStartIndex, inadimplenciaStartIndex + itemsPerPage);

    React.useEffect(() => {
        setInadimplenciaPage(1);
    }, [searchQuery]);

    const confirmAction = async () => {
        const {type, payload} = actionDialog;
        if (!payload || !type) return;

        if (type === 'bloquear-licenca') {
            await onBlockLicense?.(payload.licenca?.id || payload.licenca_id);
        }

        if (type === 'reativar-licenca') {
            await onReactivateLicense?.(payload.licenca?.id || payload.licenca_id);
        }

        if (type === 'notificar-cobranca') {
            await onNotifyCollection?.(payload.fatura_id || payload.id);
        }

        setActionDialog({open: false, type: null, payload: null});
    };

    return (
        <Stack spacing={2.5}>
            <Alert
                severity="error"
                icon={<AlertTriangle size={18} />}
                sx={{
                    border: '1px solid #fecaca',
                    bgcolor: '#fef2f2',
                    color: '#991b1b',
                    borderRadius: 2,
                }}
            >
                {filteredInadimplentes.length} {filteredInadimplentes.length === 1 ? 'empresa inadimplente' : 'empresas inadimplentes'} requerem atenção imediata.
            </Alert>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                <TextField
                    placeholder="Buscar por empresa..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                        },
                    }}
                />
                <Button variant="outlined" startIcon={<Filter size={18} />} sx={{minWidth: 140, borderRadius: 2}}>
                    Pesquisa
                </Button>
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    display: {xs: 'none', md: 'block'},
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    borderColor: '#e5e7eb',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Box sx={{overflowX: 'auto'}}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{bgcolor: '#f9fafb'}}>
                                <TableCell sx={tableHeaderCellSx}>Empresa</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Licença</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Dias em Atraso</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Severidade</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Valor Devido</TableCell>
                                <TableCell sx={tableHeaderCellSx}>Status Licença</TableCell>
                                <TableCell align="right" sx={tableHeaderCellSx}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {inadimplenciaLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{py: 6}}>
                                        Carregando inadimplência...
                                    </TableCell>
                                </TableRow>
                            ) : !paginatedInadimplentes.length ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{py: 6}}>
                                        Nenhuma empresa inadimplente encontrada.
                                    </TableCell>
                                </TableRow>
                            ) : paginatedInadimplentes.map((item) => {
                                const severity = getInadimplenciaSeverity(item.diasAtraso);
                                const licensePalette = getLicencaStatusPalette(item.licenca?.status);

                                return (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Typography sx={{color: '#111827', fontWeight: 500}}>{item.empresa_nome || item.empresa?.nome || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.empresa_id || '-'}</Typography>
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Typography variant="body2">{item.licenca_id || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Fatura: {item.fatura_id || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Typography variant="h6" sx={{color: '#dc2626', fontWeight: 700}}>{item.diasAtraso}</Typography>
                                            <Typography variant="caption" color="text.secondary">dias</Typography>
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Chip
                                                size="small"
                                                label={severity.label}
                                                sx={{bgcolor: severity.bg, color: severity.color, border: `1px solid ${severity.border}`}}
                                            />
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Typography>{formatMoney(item.valor_total)}</Typography>
                                            <Typography variant="caption" color="text.secondary">Venceu em {formatDateBR(item.vencimento)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tableBodyCellSx}>
                                            <Chip
                                                size="small"
                                                label={(item.licenca_status || item.licenca?.status || '-').replace('_', ' ')}
                                                sx={{bgcolor: licensePalette.bg, color: licensePalette.color}}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={tableBodyCellSx}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setActionDialog({open: true, type: 'notificar-cobranca', payload: item})}
                                                    sx={{color: '#2563eb'}}
                                                >
                                                    <Bell size={16} />
                                                </IconButton>
                                                {item.licenca?.status === 'ATIVA' || item.licenca?.status === 'EM_ATRASO' ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setActionDialog({open: true, type: 'bloquear-licenca', payload: item})}
                                                        sx={{color: '#b91c1c'}}
                                                    >
                                                        <Ban size={16} />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setActionDialog({open: true, type: 'reativar-licenca', payload: item})}
                                                        sx={{color: '#15803d'}}
                                                    >
                                                        <CheckCircle size={16} />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Box sx={{display: {xs: 'grid', md: 'none'}, gap: 2}}>
                {inadimplenciaLoading ? (
                    <Paper variant="outlined" sx={{p: 3, borderRadius: 2.5, textAlign: 'center'}}>
                        <Typography color="text.secondary">Carregando inadimplência...</Typography>
                    </Paper>
                ) : !paginatedInadimplentes.length ? (
                    <Paper variant="outlined" sx={{p: 3, borderRadius: 2.5, textAlign: 'center'}}>
                        <Typography color="text.secondary">Nenhuma empresa inadimplente encontrada.</Typography>
                    </Paper>
                ) : paginatedInadimplentes.map((item) => {
                    const severity = getInadimplenciaSeverity(item.diasAtraso);
                    const licensePalette = getLicencaStatusPalette(item.licenca?.status);

                    return (
                        <Card key={item.id} variant="outlined" sx={{borderRadius: 2.5, borderColor: '#e5e7eb'}}>
                            <CardContent sx={{p: 2.25}}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                        <Box>
                                            <Typography sx={{color: '#111827', fontWeight: 600}}>{item.empresa_nome || item.empresa?.nome || '-'}</Typography>
                                            <Typography variant="body2" color="text.secondary">{item.empresa_id || '-'}</Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={severity.label}
                                            sx={{bgcolor: severity.bg, color: severity.color, border: `1px solid ${severity.border}`}}
                                        />
                                    </Stack>
                                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Dias em atraso</Typography>
                                            <Typography sx={{color: '#dc2626', fontWeight: 700}}>{item.diasAtraso} dias</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Valor devido</Typography>
                                            <Typography>{formatMoney(item.valor_total)}</Typography>
                                        </Box>
                                        <Box sx={{gridColumn: '1 / -1'}}>
                                            <Typography variant="caption" color="text.secondary">Licença</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5}}>
                                                <Typography variant="body2">{item.licenca_id || '-'}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={(item.licenca_status || item.licenca?.status || '-').replace('_', ' ')}
                                                    sx={{bgcolor: licensePalette.bg, color: licensePalette.color}}
                                                />
                                            </Stack>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button variant="outlined" size="small" onClick={() => setActionDialog({open: true, type: 'notificar-cobranca', payload: item})}>
                                            Notificar
                                        </Button>
                                        {item.licenca?.status === 'ATIVA' || item.licenca?.status === 'EM_ATRASO' ? (
                                            <Button color="error" variant="contained" size="small" onClick={() => setActionDialog({open: true, type: 'bloquear-licenca', payload: item})}>
                                                Bloquear
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}
                                                onClick={() => setActionDialog({open: true, type: 'reativar-licenca', payload: item})}
                                            >
                                                Reativar
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', sm: 'center'}} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    Mostrando {filteredInadimplentes.length ? inadimplenciaStartIndex + 1 : 0} a {Math.min(inadimplenciaStartIndex + itemsPerPage, filteredInadimplentes.length)} de {filteredInadimplentes.length} registros
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton disabled={safeInadimplenciaPage === 1} onClick={() => setInadimplenciaPage((current) => Math.max(1, current - 1))}>
                        <ChevronLeft size={18} />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                        Página {safeInadimplenciaPage} de {inadimplenciaTotalPages}
                    </Typography>
                    <IconButton disabled={safeInadimplenciaPage === inadimplenciaTotalPages} onClick={() => setInadimplenciaPage((current) => Math.min(inadimplenciaTotalPages, current + 1))}>
                        <ChevronRight size={18} />
                    </IconButton>
                </Stack>
            </Stack>

            <ActionConfirmationDialog
                open={actionDialog.open}
                title={
                    actionDialog.type === 'bloquear-licenca' ? 'Bloquear Licença'
                        : actionDialog.type === 'reativar-licenca' ? 'Reativar Licença'
                            : 'Enviar Notificação'
                }
                message={
                    actionDialog.type === 'bloquear-licenca'
                        ? `Tem certeza que deseja bloquear a licença da empresa "${actionDialog.payload?.empresa_nome || actionDialog.payload?.empresa?.nome}"?`
                        : actionDialog.type === 'reativar-licenca'
                            ? `Deseja reativar a licença da empresa "${actionDialog.payload?.empresa_nome || actionDialog.payload?.empresa?.nome}"?`
                            : `Enviar notificação de cobrança para "${actionDialog.payload?.empresa_nome || actionDialog.payload?.empresa?.nome}"?`
                }
                confirmLabel={
                    actionDialog.type === 'bloquear-licenca' ? 'Bloquear'
                        : actionDialog.type === 'reativar-licenca' ? 'Reativar'
                            : 'Enviar'
                }
                confirmColor={actionDialog.type === 'bloquear-licenca' ? 'error' : 'primary'}
                onCancel={() => setActionDialog({open: false, type: null, payload: null})}
                onConfirm={confirmAction}
            />
        </Stack>
    );
}
